import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import gql from 'graphql-tag';
import { useMutation, useLazyQuery, useSubscription } from '@apollo/client';
import { DropzoneDialog } from 'mui-file-dropzone';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import MuiButton from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';

import { DocumentContext } from '../../../contexts/DocumentContext';
import { accountSelectors } from '../../../redux/accountReducer';
import { CreditSubmissionParsingRunStatus } from '@trnsact/trnsact-shared-types';

// ── GraphQL ────────────────────────────────────────────────────────────────────

const PROCESS_DECISION_LETTER = gql`
  mutation ProcessDecisionLetterViaWorkflow($input: ProcessDecisionLetterInput!, $file: Upload!) {
    processDecisionLetterViaWorkflow(input: $input, file: $file) {
      n8nExecutionId
    }
  }
`;

// Fallback: classic upsertDocument with delegateToWorkflow: false
const UPSERT_DOCUMENT = gql`
  mutation UpsertDocumentFallback($input: UpsertDocumentInput!, $file: Upload) {
    upsertDocument(input: $input, file: $file)
  }
`;

const PARSING_RUN_UPDATED = gql`
  subscription ParsingRunUpdatedByExecutionId($n8nExecutionId: ID!) {
    creditSubmissionParsingRunUpdated(n8nExecutionId: $n8nExecutionId) {
      parsingRunId
      n8nExecutionId
      documentId
      creditSubmissionId
      vendorOpportunityId
      parsedDecisionId
      status
      errorMessage
      createdDateTime
      modifiedDateTime
    }
  }
`;

const GET_CREDIT_SUBMISSIONS = gql`
  query CreditSubmissionsForManualLink($id: ID!) {
    creditSubmissionsByDynamicsVOId(id: $id) {
      creditSubmissionId
      creditSubId
      lenderName
      submittedBy
      submissionDate
    }
  }
`;

// ── Types ──────────────────────────────────────────────────────────────────────

interface DecisionLetterUploadModalProps {
  open: boolean;
  onClose: () => void;
  onCompleted?: () => void;
  vo: {
    vendorOpportunityId: string;
    [key: string]: unknown;
  };
  dynamicsVoId?: string;
}

// ── Constants ──────────────────────────────────────────────────────────────────

const PARSING_STEPS = ['Document Received', 'Parsing', 'Linking', 'Completed'];

const TERMINAL_STATUSES = new Set([
  CreditSubmissionParsingRunStatus.Completed,
  CreditSubmissionParsingRunStatus.Unlinked,
  CreditSubmissionParsingRunStatus.Failed,
]);

function getParsingActiveStep(status: string): number {
  switch (status) {
    case CreditSubmissionParsingRunStatus.Pending:
      return 0;
    case CreditSubmissionParsingRunStatus.DocumentReceived:
      return 1;
    case CreditSubmissionParsingRunStatus.Parsing:
      return 2;
    case CreditSubmissionParsingRunStatus.Linking:
      return 3;
    case CreditSubmissionParsingRunStatus.Completed:
      return 4;
    case CreditSubmissionParsingRunStatus.Unlinked:
    case CreditSubmissionParsingRunStatus.Failed:
      return 3;
    default:
      return 0;
  }
}

// ── Component ──────────────────────────────────────────────────────────────────

export const DecisionLetterUploadModal: React.FC<DecisionLetterUploadModalProps> = ({
  open,
  onClose,
  onCompleted,
  vo,
  dynamicsVoId,
}) => {
  const userProfile = useSelector((state: any) => state.userProfile);
  const accountType = useSelector(accountSelectors.accountType);
  const isDealer = accountType === 'DEALER';
  const documentContext = useContext(DocumentContext) as any;

  // n8nExecutionId is set after processDecisionLetterViaWorkflow returns —
  // the subscription below activates only when this is non-null.
  const [n8nExecutionId, setN8nExecutionId] = useState<string | null>(null);
  const [parsingRun, setParsingRun] = useState<any>(null);
  const [showParsingDialog, setShowParsingDialog] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [workflowStartError, setWorkflowStartError] = useState<string | null>(null);
  const [originalFile, setOriginalFile] = useState<File | null>(null);
  const [completionHandled, setCompletionHandled] = useState(false);

  // Fallback state (UNLINKED / FAILED / mutation error)
  const [selectedCreditSubId, setSelectedCreditSubId] = useState<string>('');
  const [fallbackRequested, setFallbackRequested] = useState(false);
  const [fallbackUploading, setFallbackUploading] = useState(false);

  const [processDecisionLetter] = useMutation(PROCESS_DECISION_LETTER, {
    context: { authRequired: true },
  });

  const [upsertDocument] = useMutation(UPSERT_DOCUMENT, {
    context: { authRequired: true },
  });

  const [getCreditSubmissions, { data: creditSubmissionsData, loading: creditSubmissionsLoading }] =
    useLazyQuery(GET_CREDIT_SUBMISSIONS);

  useSubscription(PARSING_RUN_UPDATED, {
    variables: { n8nExecutionId: n8nExecutionId! },
    skip: !n8nExecutionId,
    onData: ({ data }: { data: any }) => {
      const run = data.data?.creditSubmissionParsingRunUpdated;
      if (run) {
        console.log('[DecisionLetterUploadModal] parsing run update:', run);
        setParsingRun(run);

        if (run.status === CreditSubmissionParsingRunStatus.Completed && !completionHandled) {
          setCompletionHandled(true);
          handleCloseAll();
          onCompleted?.();
        }

        if (
          [CreditSubmissionParsingRunStatus.Unlinked, CreditSubmissionParsingRunStatus.Failed].includes(run.status) &&
          !fallbackRequested
        ) {
          setFallbackRequested(true);
        }
      }
    },
  });

  // ── Primary upload ──────────────────────────────────────────────────────────

  const handleSaveFile = async (uploadFiles: File[]) => {
    if (!uploadFiles.length) return;

    const file = uploadFiles[0];
    setOriginalFile(file);
    setParsingRun(null);
    setN8nExecutionId(null);
    setSelectedCreditSubId('');
    setFallbackRequested(false);
    setWorkflowStartError(null);
    setCompletionHandled(false);
    setUploading(true);
    setShowParsingDialog(true);

    // Pre-fetch submissions in the background in case we need the fallback.
    if (dynamicsVoId) {
      getCreditSubmissions({ variables: { id: dynamicsVoId } });
    }

    try {
      const result = await processDecisionLetter({
        variables: {
          input: {
            vendorOpportunityId: vo.vendorOpportunityId,
            docName: file.name,
          },
          file,
        },
      });

      const executionId = result.data?.processDecisionLetterViaWorkflow?.n8nExecutionId;
      if (executionId) {
        setN8nExecutionId(executionId);
      }
    } catch (err) {
      console.error('[DecisionLetterUploadModal] processDecisionLetterViaWorkflow error:', err);
      setWorkflowStartError('Failed to start automated workflow. Falling back to manual upload.');
      setFallbackRequested(true);
    } finally {
      setUploading(false);
    }
  };

  // ── Fallback: manual upsertDocument (no workflow) ──────────────────────────

  const handleFallbackSubmit = useCallback(
    async (creditSubId?: string) => {
      if (!originalFile || fallbackUploading) return;

      setFallbackUploading(true);
      try {
        const response = await upsertDocument({
          variables: {
            input: {
              vendorOpportunityId: vo.vendorOpportunityId,
              source: originalFile.name,
              docType: 'decision letter',
              docDescription: '',
              createdBy: userProfile.fullName || 'Trnsact',
              docName: originalFile.name,
              notifyUserProfileIds: [],
              delegateToWorkflow: false,
              ...(creditSubId ? { creditSubId } : {}),
              ...(isDealer ? { createdByAccount: userProfile.accountId } : {}),
            },
            file: originalFile,
          },
        });
        await documentContext?.setLastDocument(response.data.upsertDocument);
        onCompleted?.();
        setShowParsingDialog(false);
        setParsingRun(null);
        setN8nExecutionId(null);
        setSelectedCreditSubId('');
        setFallbackRequested(false);
        setWorkflowStartError(null);
        setOriginalFile(null);
        setCompletionHandled(false);
      } catch (err) {
        console.error('[DecisionLetterUploadModal] fallback upsertDocument error:', err);
      } finally {
        setFallbackUploading(false);
      }
    },
    [documentContext, fallbackUploading, isDealer, onCompleted, originalFile, upsertDocument, userProfile, vo]
  );

  // ── Helpers ─────────────────────────────────────────────────────────────────

  const handleCloseParsingDialog = () => {
    setShowParsingDialog(false);
    setParsingRun(null);
    setN8nExecutionId(null);
    setSelectedCreditSubId('');
    setFallbackRequested(false);
    setWorkflowStartError(null);
    setOriginalFile(null);
    setCompletionHandled(false);
  };

  const handleCloseAll = () => {
    handleCloseParsingDialog();
  };

  const isParsingTerminal = parsingRun && TERMINAL_STATUSES.has(parsingRun.status);
  const isUnlinked = parsingRun?.status === CreditSubmissionParsingRunStatus.Unlinked;
  const isFailed = parsingRun?.status === CreditSubmissionParsingRunStatus.Failed;
  const isParsingError = isUnlinked || isFailed;
  const shouldShowFallback = fallbackRequested || fallbackUploading;

  const creditSubmissions: any[] = creditSubmissionsData?.creditSubmissionsByDynamicsVOId?.filter(Boolean) ?? [];

  const shouldPickSubmission = shouldShowFallback && !creditSubmissionsLoading && creditSubmissions.length > 0;

  const canAutoFallbackWithoutSelection =
    shouldShowFallback && !creditSubmissionsLoading && creditSubmissions.length === 0 && !!originalFile;

  useEffect(() => {
    if (canAutoFallbackWithoutSelection && !fallbackUploading) {
      // No submissions to choose from; continue immediately with plain upsert.
      handleFallbackSubmit();
    }
  }, [canAutoFallbackWithoutSelection, fallbackUploading, handleFallbackSubmit]);

  useEffect(() => {
    if (shouldPickSubmission && selectedCreditSubId && !fallbackUploading) {
      // As soon as the user selects a submission, continue automatically.
      handleFallbackSubmit(selectedCreditSubId);
    }
  }, [fallbackUploading, handleFallbackSubmit, selectedCreditSubId, shouldPickSubmission]);

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <>
      {/* Primary upload dropzone */}
      <DropzoneDialog
        open={open}
        onSave={files => {
          handleSaveFile(files as unknown as File[]);
          onClose();
        }}
        onClose={onClose}
        fileObjects={[]}
        filesLimit={1}
        dropzoneText="Drag & drop or choose a decision letter file"
        dialogTitle="Upload Decision Letter"
        showPreviews
        maxFileSize={25_000_000}
        acceptedFiles={['.pdf', '.doc', '.docx']}
      />

      {/* Parsing progress / fallback dialog */}
      <Dialog open={showParsingDialog} onClose={handleCloseParsingDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Processing Decision Letter</DialogTitle>

        <DialogContent>
          <Box sx={{ py: 1 }}>
            {uploading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <CircularProgress size={20} />
                <Typography variant="body2">Sending to workflow…</Typography>
              </Box>
            ) : workflowStartError ? (
              <Alert severity="warning">{workflowStartError}</Alert>
            ) : !parsingRun ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <CircularProgress size={20} />
                <Typography variant="body2">Waiting for workflow to start…</Typography>
              </Box>
            ) : (
              <>
                <Stepper activeStep={getParsingActiveStep(parsingRun.status)} alternativeLabel sx={{ mb: 2 }}>
                  {PARSING_STEPS.map((label, index) => {
                    const isLastStep = index === PARSING_STEPS.length - 1;
                    return (
                      <Step key={label} completed={index < getParsingActiveStep(parsingRun.status)}>
                        <StepLabel error={isLastStep && isParsingError}>{label}</StepLabel>
                      </Step>
                    );
                  })}
                </Stepper>

                {parsingRun.status === CreditSubmissionParsingRunStatus.Completed && (
                  <Alert severity="success">Decision letter successfully parsed and linked.</Alert>
                )}

                {isFailed && !isUnlinked && (
                  <Alert severity="error">{parsingRun.errorMessage || 'An error occurred during parsing.'}</Alert>
                )}

                {(isParsingError || shouldShowFallback) && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                    {isUnlinked ? (
                      <Alert severity="warning">The document was parsed but could not be linked automatically.</Alert>
                    ) : (
                      <Alert severity="info">
                        You can still upload the file manually and link it to a credit submission.
                      </Alert>
                    )}

                    {shouldPickSubmission && (
                      <FormControl fullWidth size="small">
                        <InputLabel>Select Credit Submission</InputLabel>
                        <Select
                          value={selectedCreditSubId}
                          label="Select Credit Submission"
                          onChange={e => setSelectedCreditSubId(e.target.value)}
                          disabled={fallbackUploading}
                        >
                          {creditSubmissions.map((cs: any) => (
                            <MenuItem key={cs.creditSubId} value={cs.creditSubId}>
                              {cs.lenderName} — {cs.submittedBy} @ {cs.submissionDate}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}

                    {fallbackUploading && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <CircularProgress size={20} />
                        <Typography variant="body2">Uploading same file via manual flow…</Typography>
                      </Box>
                    )}
                  </Box>
                )}

                {!isParsingTerminal && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <CircularProgress size={24} />
                  </Box>
                )}
              </>
            )}
          </Box>
        </DialogContent>

        <DialogActions>
          <MuiButton color={isParsingError ? 'inherit' : 'primary'} onClick={handleCloseParsingDialog}>
            Close
          </MuiButton>
        </DialogActions>
      </Dialog>
    </>
  );
};
